// CSS utility functions shared by EpubViewer and DecryptingResource.

/**
 * Extract the directory portion of a path (trailing slash included).
 * e.g. "OEBPS/styles/main.css" → "OEBPS/styles/"
 *      "main.css"              → ""
 */
export function dirFromPath(path) {
    const i = path.lastIndexOf('/');
    return i >= 0 ? path.substring(0, i + 1) : '';
}

/**
 * Find a CSS entry in a cache Map by a relative href.
 * Returns { css, cacheKey } or null.
 *
 * Cache keys are full publication paths (e.g. "OEBPS/styles/main.css")
 * but <link> hrefs can be relative (e.g. "../styles/main.css").
 * Resolves the mismatch via:
 * 1. Direct / normalized match  2. Suffix match  3. Filename-only match
 */
export function findCssInCacheWithKey(cache, xhtmlDir, relativeHref) {
    const normalized = resolveRelativePath(xhtmlDir, relativeHref);

    // Direct match
    if (cache.has(relativeHref)) return { css: cache.get(relativeHref), cacheKey: relativeHref };
    if (cache.has(normalized)) return { css: cache.get(normalized), cacheKey: normalized };

    // Suffix match: "OEBPS/styles/main.css" ends with "/styles/main.css"
    for (const [key, val] of cache) {
        if (key.endsWith(`/${normalized}`) || normalized.endsWith(`/${key}`)) return { css: val, cacheKey: key };
    }

    // Filename-only match
    const filename = normalized.split('/').pop();
    for (const [key, val] of cache) {
        if (key.endsWith(`/${filename}`) || key === filename) return { css: val, cacheKey: key };
    }

    return null;
}

/**
 * Resolve a relative path against a base directory.
 * e.g. resolveRelativePath("OEBPS/css/", "../fonts/f0.otf") → "OEBPS/fonts/f0.otf"
 */
export function resolveRelativePath(baseDir, relativePath) {
    const parts = baseDir.split('/').filter(Boolean);

    for (const segment of relativePath.split('/')) {
        if (segment === '..') parts.pop();
        else if (segment !== '.' && segment !== '') parts.push(segment);
    }

    return parts.join('/');
}

/**
 * Make a publication-absolute path relative to a given directory.
 * e.g. makeRelativePath("OEBPS/", "OEBPS/fonts/f0.otf") → "fonts/f0.otf"
 */
export function makeRelativePath(fromDir, toPath) {
    const fromParts = fromDir.split('/').filter(Boolean);
    const toParts = toPath.split('/').filter(Boolean);

    let common = 0;
    while (common < fromParts.length && common < toParts.length && fromParts[common] === toParts[common]) {
        common++;
    }

    const ups = fromParts.length - common;
    const remaining = toParts.slice(common);
    return [...Array(ups).fill('..'), ...remaining].join('/') || '.';
}

/**
 * Rebase relative url() references in CSS so they resolve correctly
 * when the CSS is inlined into an XHTML file at a different path.
 *
 * e.g. CSS at "OEBPS/css/FontSrc.css" with url('../fonts/f0.otf')
 *      inlined into "OEBPS/cover.xhtml"
 *      → url('fonts/f0.otf') (relative to OEBPS/)
 */
export function rebaseCssUrls(css, cssAbsPath, xhtmlDir) {
    const cssDir = dirFromPath(cssAbsPath);

    if (cssDir === xhtmlDir) return css;

    return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, quote, rawUrl) => {
        if (
            rawUrl.startsWith('data:') ||
            rawUrl.startsWith('http://') ||
            rawUrl.startsWith('https://') ||
            rawUrl.startsWith('//') ||
            rawUrl.startsWith('blob:')
        ) {
            return match;
        }

        const absPath = resolveRelativePath(cssDir, rawUrl);
        const rebasedUrl = makeRelativePath(xhtmlDir, absPath);

        return `url(${quote}${rebasedUrl}${quote})`;
    });
}

/**
 * Async, recursive @import resolver. Imported CSS is fetched via `getCss`
 * (a function returning Promise<{css, cacheKey} | null>), recursively resolved
 * for its own @imports, rebased against the importer directory, and inlined.
 *
 * Cycle detection uses the `visited` set keyed by publication-absolute paths.
 * Already-visited keys are dropped from the output so the browser does not
 * re-enter a cycle. Imports that fail to resolve leave the original
 * @import statement untouched (browser will then fail loudly in dev tools,
 * matching today's behaviour for missing CSS).
 *
 * @param {string} cssText
 * @param {string} importerAbsPath — publication-absolute path of the CSS being processed
 * @param {(absHref: string, requesterDir: string) => Promise<{css: string, cacheKey: string} | null>} getCss
 * @param {Set<string>} visited — set of publication-absolute paths already in the chain
 * @returns {Promise<string>}
 */
export async function resolveCssImports(cssText, importerAbsPath, getCss, visited = new Set()) {
    if (!cssText.includes('@import')) return cssText;

    const importerDir = dirFromPath(importerAbsPath);
    const importRegex = /@import\s+(?:url\(\s*(['"]?)([^'")]+)\1\s*\)|(['"])([^'"]+)\3)\s*([^;]*);\s*/gi;

    // Run @import regex against the original text (not a decommented copy)
    // so block comments survive into the cached output. Comment ranges are
    // tracked separately to skip @import-like tokens inside `/* ... */`.
    const commentRegex = /\/\*[\s\S]*?\*\//g;
    const commentRanges = [];
    for (const c of cssText.matchAll(commentRegex)) {
        commentRanges.push([c.index, c.index + c[0].length]);
    }
    const insideComment = offset => commentRanges.some(([s, e]) => offset >= s && offset < e);

    const matches = [...cssText.matchAll(importRegex)].filter(m => !insideComment(m.index));

    // Resolve in parallel; cycles guarded via the visited set.
    const resolved = await Promise.all(
        matches.map(async m => {
            const [matchText, _uq, urlPath, _sq, strPath, mediaQuery] = m;
            const start = m.index;
            const end = m.index + matchText.length;
            const importPath = urlPath || strPath;
            if (!importPath) return { start, end, replacement: matchText };

            const pathWithoutQuery = importPath.split('?')[0].split('#')[0];
            if (!pathWithoutQuery.endsWith('.css')) return { start, end, replacement: matchText };

            const result = await getCss(pathWithoutQuery, importerDir);
            if (!result) {
                console.warn('Imported CSS not resolvable:', importPath, 'from', importerAbsPath);
                return { start, end, replacement: matchText };
            }
            if (result.cyclic || visited.has(result.cacheKey)) {
                // Cycle: drop the @import to break the loop.
                return { start, end, replacement: '' };
            }

            const childVisited = new Set(visited).add(result.cacheKey);
            const childResolved = await resolveCssImports(result.css, result.cacheKey, getCss, childVisited);
            const rebased = rebaseCssUrls(childResolved, result.cacheKey, importerDir);

            const trimmedMedia = mediaQuery?.trim();
            const wrapped = trimmedMedia ? `@media ${trimmedMedia} {\n${rebased}\n}\n` : `${rebased}\n`;
            return { start, end, replacement: wrapped };
        })
    );

    // Splice from the end so earlier offsets remain valid as we mutate.
    let output = cssText;
    for (let i = resolved.length - 1; i >= 0; i--) {
        const { start, end, replacement } = resolved[i];
        output = output.slice(0, start) + replacement + output.slice(end);
    }
    return output;
}
